import Vue from 'vue';
import axios from 'axios';
import type { IFiabilityStudy } from '@/shared/model/fiability-study.model';
import FiabilityStudyService, { type IValidationError } from '@/entities/fiability-study/fiabilityStudy.service';
import NotificationService from './notification.service';

export interface IWorkflowResult {
  success: boolean;
  errors?: IValidationError[];
  warnings?: IValidationError[];
  study?: IFiabilityStudy;
}

/**
 * Service managing study validation workflow transitions.
 * Centralizes validation logic duplicated across StudyCreationForm and study-item.
 */
export default class StudyValidationWorkflowService {
  private fiabilityStudyService: FiabilityStudyService;
  private notificationService: NotificationService;

  constructor() {
    this.fiabilityStudyService = new FiabilityStudyService();
    this.notificationService = new NotificationService();
  }

  /**
   * Submit study for validation (DRAFT -> VALIDATION)
   */
  public async submitForValidation(instance: Vue, study: IFiabilityStudy): Promise<IWorkflowResult> {
    try {
      const result = await this.fiabilityStudyService.submitForValidation(study.id);

      if (result.valid) {
        // Submission accepted — re-fetch study to get updated status
        const updatedStudy = await this.fiabilityStudyService.find(study.id);
        this.notificationService.showSuccess(instance, 'Étude soumise pour validation');
        return { success: true, study: updatedStudy };
      } else {
        return { success: false, errors: result.errors, warnings: result.warnings };
      }
    } catch (error) {
      console.error('Error submitting study for validation:', error);
      this.notificationService.handleApiError(instance, error, 'Erreur lors de la soumission pour validation');
      return { success: false };
    }
  }

  /**
   * Validate study (VALIDATION -> VALIDATED)
   */
  public async validateStudy(instance: Vue, studyId: number): Promise<IWorkflowResult> {
    try {
      const response = await axios.post(`/api/fiabilityStudies/${studyId}/validate`);
      const updatedStudy = response.data;

      this.notificationService.showSuccess(instance, 'Étude validée avec succès');
      return { success: true, study: updatedStudy };
    } catch (error) {
      console.error('Error validating study:', error);
      this.notificationService.handleApiError(instance, error, "Erreur lors de la validation de l'étude");
      return { success: false };
    }
  }

  /**
   * Reject study back to draft (VALIDATION -> DRAFT)
   */
  public async rejectStudy(instance: Vue, studyId: number): Promise<IWorkflowResult> {
    try {
      const response = await axios.post(`/api/fiabilityStudies/${studyId}/reject`);
      const updatedStudy = response.data;

      this.notificationService.showInfo(instance, 'Étude renvoyée en brouillon');
      return { success: true, study: updatedStudy };
    } catch (error) {
      console.error('Error rejecting study:', error);
      this.notificationService.handleApiError(instance, error, "Erreur lors du rejet de l'étude");
      return { success: false };
    }
  }

  /**
   * Unlock validated study (VALIDATED -> DRAFT)
   */
  public async unlockStudy(instance: Vue, studyId: number, justification: string, userId: string): Promise<IWorkflowResult> {
    try {
      const response = await axios.post(`/api/fiabilityStudies/${studyId}/unlock`, {
        userId,
        justification,
      });
      const updatedStudy = response.data;

      this.notificationService.showSuccess(instance, 'Étude déverrouillée avec succès');
      return { success: true, study: updatedStudy };
    } catch (error) {
      console.error('Error unlocking study:', error);
      this.notificationService.handleApiError(instance, error, "Erreur lors du déverrouillage de l'étude");
      return { success: false };
    }
  }
}
